import { useState } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import { TC, FS, FW } from "../../tokens/text";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Icon from "@mui/material/Icon";

// ─── Issue data ───────────────────────────────────────────────────────────────

const ISSUES = [
  {
    id: "ISS-001",
    date: "2026-05-03",
    severity: "high",
    status: "resolved",
    title: "User cannot log in after account is deleted and recreated in Firebase",
    description:
      "When a Firebase Auth user is deleted and recreated (e.g. password reset via Firebase console), the new Firebase UID does not match the UID stored in the Firestore tenant users collection. The app reads role, tenantId, and permissions from both Firebase custom claims and Firestore — both are keyed on UID. A mismatch causes the login to succeed at the Firebase level but the app sees no tenant association, resulting in a redirect to /unauthorized or loss of all permissions.",
    symptoms: [
      "User can authenticate but is redirected to /unauthorized",
      "App shows no tenant, no client data, no permissions",
      "Firebase Auth shows the user but Firestore has the old UID",
      "Custom claims (role, tenantId) missing from the new token",
    ],
    rootCause:
      "Firebase generates a new UID each time a user is created, even for the same email address. Deleting and recreating an account breaks all Firestore references that used the old UID as the document ID.",
    fix: [
      "Use the Firebase Admin SDK to look up the new UID for the email: admin.auth().getUserByEmail(email)",
      "In Firestore, copy the existing user document (companies/{tenantId}/users/{oldUid}) to a new document with the new UID as the key, then delete the old document.",
      "Set custom claims on the new UID using admin.auth().setCustomUserClaims(newUid, { role, tenantId, permissions, assignedRoleIds }).",
      "Ask the user to sign out and sign back in so the browser fetches a fresh token with the updated claims.",
      "Prevention: avoid deleting Firebase users. Use admin.auth().updateUser(uid, { password }) to reset passwords — this preserves the UID.",
    ],
    resolvedBy: "Vijithan Kulaweerasingam",
  },
  {
    id: "ISS-002",
    date: "2026-06-06",
    severity: "medium",
    status: "resolved",
    title: "Assign kathir@gmail.com to tenant 'ledgerhub-e7dca7' / make tenant_admin",
    description:
      "Guide to add an existing user (kathir@gmail.com) as tenant_admin for tenantId 'ledgerhub-e7dca7'. This fixes missing tenant association or permission issues for that user.",
    symptoms: [
      "User cannot access tenant-scoped pages",
      "User needs tenant admin privileges to manage employees",
    ],
    rootCause:
      "User exists in Firebase Auth but does not have custom claims set for the target tenant (role, tenantId).",
    fix: [
      "Use the included helper script from the repo to set tenant admin claims:",
      "  cd accounting-api",
      "  node scripts/set-tenant-admin-claims.js kathir@gmail.com ledgerhub-e7dca7",
      "Alternative (Admin SDK):",
      "  const user = await admin.auth().getUserByEmail('kathir@gmail.com')",
      "  await admin.auth().setCustomUserClaims(user.uid, { role: 'tenant_admin', tenantId: 'ledgerhub-e7dca7', assignedRoleIds: [], permissions: [] })",
      "Ask the user to sign out and sign back in so their token refreshes and the UI reflects the new tenant association.",
    ],
    scriptLines: [
      "# set-tenant-admin-claims.sh",
      "Usage:",
      "  cd accounting-api",
      "  node scripts/set-tenant-admin-claims.js <email> <tenantId> [permissions]",
      "",
      "Example:",
      "  node scripts/set-tenant-admin-claims.js kathir@gmail.com ledgerhub-e7dca7",
      "",
      "Or using Firebase Admin SDK (Node):",
      "  const user = await admin.auth().getUserByEmail('kathir@gmail.com');",
      "  await admin.auth().setCustomUserClaims(user.uid, { role: 'tenant_admin', tenantId: 'ledgerhub-e7dca7', assignedRoleIds: [], permissions: [] });",
      "",
      "Notes: requires Admin SDK credentials (FIREBASE_SERVICE_ACCOUNT_JSON or GOOGLE_APPLICATION_CREDENTIALS).",
    ],
    resolvedBy: "Automated script (local run)",
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const SEVERITY_COLOURS = {
  critical: { bg: "#fff0f0", border: "#ffcdd2", text: TC.error,   label: "Critical" },
  high:     { bg: "#fff8e1", border: "#ffe082", text: "#e65100",  label: "High"     },
  medium:   { bg: "#e8f5e9", border: "#a5d6a7", text: "#2e7d32",  label: "Medium"   },
  low:      { bg: "#e3f2fd", border: "#90caf9", text: TC.link,    label: "Low"      },
};

const STATUS_COLOURS = {
  resolved:    { bg: "#e8f5e9", text: TC.success,   label: "Resolved"    },
  open:        { bg: "#fff8e1", text: "#e65100",    label: "Open"        },
  in_progress: { bg: "#e3f2fd", text: TC.link,      label: "In Progress" },
};

function SeverityChip({ severity }) {
  const c = SEVERITY_COLOURS[severity] || SEVERITY_COLOURS.low;
  return (
    <span style={{
      padding: "2px 10px", borderRadius: 20, fontSize: FS.xs, fontWeight: FW.semibold,
      background: c.bg, border: `1px solid ${c.border}`, color: c.text,
    }}>
      {c.label}
    </span>
  );
}

function StatusChip({ status }) {
  const c = STATUS_COLOURS[status] || STATUS_COLOURS.open;
  return (
    <span style={{
      padding: "2px 10px", borderRadius: 20, fontSize: FS.xs, fontWeight: FW.semibold,
      background: c.bg, color: c.text,
    }}>
      {c.label}
    </span>
  );
}

function IssueCard({ issue }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{
      background: "#ffffff",
      border: "1px solid #d8e6f7",
      borderRadius: 12,
      marginBottom: 16,
      overflow: "hidden",
      boxShadow: "0 1px 6px rgba(10,22,40,0.06)",
    }}>
      {/* Header row */}
      <div
        onClick={() => setExpanded(v => !v)}
        style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "14px 18px", cursor: "pointer",
          background: expanded ? "#f4f8ff" : "#ffffff",
          transition: "background 0.15s",
        }}
      >
        <div style={{
          width: 36, height: 36, borderRadius: "50%",
          background: issue.severity === "high" ? "#fff8e1" : "#e3f2fd",
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <Icon style={{ fontSize: 18, color: issue.severity === "high" ? "#e65100" : TC.link }}>
            bug_report
          </Icon>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 3 }}>
            <span style={{ fontSize: FS.xs, color: TC.tertiary, fontWeight: FW.medium }}>{issue.id}</span>
            <SeverityChip severity={issue.severity} />
            <StatusChip status={issue.status} />
            <span style={{ fontSize: FS.xs, color: TC.tertiary, marginLeft: "auto" }}>{issue.date}</span>
          </div>
          <div style={{ fontSize: FS.md, fontWeight: FW.semibold, color: TC.primary, lineHeight: 1.3 }}>
            {issue.title}
          </div>
        </div>

        <Icon style={{ fontSize: 20, color: TC.tertiary, flexShrink: 0, transition: "transform 0.2s", transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}>
          expand_more
        </Icon>
      </div>

      {/* Expanded body */}
      <Collapse in={expanded}>
        <div style={{ padding: "0 18px 18px 18px", borderTop: "1px solid #e8f0fb" }}>

          {/* Description */}
          <Section title="Description" icon="info">
            <p style={{ fontSize: FS.sm, color: TC.secondary, lineHeight: 1.7, margin: 0 }}>
              {issue.description}
            </p>
          </Section>

          {/* Symptoms */}
          <Section title="Symptoms" icon="warning_amber">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {issue.symptoms.map((s, i) => (
                <li key={i} style={{ fontSize: FS.sm, color: TC.secondary, lineHeight: 1.7 }}>{s}</li>
              ))}
            </ul>
          </Section>

          {/* Root cause */}
          <Section title="Root Cause" icon="manage_search">
            <p style={{ fontSize: FS.sm, color: TC.secondary, lineHeight: 1.7, margin: 0 }}>
              {issue.rootCause}
            </p>
          </Section>

          {/* Fix */}
          <Section title="How to Fix" icon="build_circle" accent>
            <ol style={{ margin: 0, paddingLeft: 20 }}>
              {issue.fix.map((step, i) => (
                <li key={i} style={{ fontSize: FS.sm, color: TC.primary, lineHeight: 1.8, fontWeight: FW.medium }}>
                  {step}
                </li>
              ))}
            </ol>
          </Section>

          {issue.scriptLines && (
            <Section title="Script (set-tenant-admin-claims.js)" icon="code">
              <pre style={{ whiteSpace: 'pre-wrap', fontSize: FS.sm, color: '#e6eef8', margin: 0, background: '#0b1220', padding: 12, borderRadius: 8, overflowX: 'auto' }}>
{`<script>\n${issue.scriptLines.join('\n')}\n</script>`}
              </pre>
            </Section>
          )}

          {issue.resolvedBy && (
            <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 6 }}>
              <Icon style={{ fontSize: 15, color: TC.success }}>check_circle</Icon>
              <span style={{ fontSize: FS.xs, color: TC.tertiary }}>
                Resolved by <strong style={{ color: TC.secondary }}>{issue.resolvedBy}</strong>
              </span>
            </div>
          )}
        </div>
      </Collapse>
    </div>
  );
}

function Section({ title, icon, accent, children }) {
  return (
    <div style={{
      marginTop: 14,
      background: accent ? "#f0fff4" : "#fafcff",
      border: `1px solid ${accent ? "#c8e6c9" : "#e8f0fb"}`,
      borderRadius: 8,
      padding: "10px 14px",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
        <Icon style={{ fontSize: 15, color: accent ? TC.success : TC.link }}>{icon}</Icon>
        <span style={{ fontSize: FS.xs, fontWeight: FW.semibold, color: accent ? TC.success : TC.link, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          {title}
        </span>
      </div>
      {children}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function IssuesFixesPage() {
  const [filter, setFilter] = useState("all");

  const filtered = filter === "all"
    ? ISSUES
    : ISSUES.filter(i => i.status === filter || i.severity === filter);

  const counts = {
    total: ISSUES.length,
    open: ISSUES.filter(i => i.status === "open").length,
    resolved: ISSUES.filter(i => i.status === "resolved").length,
    high: ISSUES.filter(i => i.severity === "critical" || i.severity === "high").length,
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3} px={2}>

        {/* Page header */}
        <VuiBox mb={3}>
          <VuiTypography style={{ fontSize: FS.xl, fontWeight: FW.bold, color: TC.primary }}>
            Issues &amp; Fixes
          </VuiTypography>
          <VuiTypography style={{ fontSize: FS.sm, color: TC.secondary, marginTop: 4 }}>
            Known issues, root causes, and resolution steps for Ledger Vision.
          </VuiTypography>
        </VuiBox>

        {/* Summary cards */}
        <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
          {[
            { label: "Total Issues", value: counts.total,    icon: "list_alt",      bg: "#f4f8ff", color: TC.link    },
            { label: "Open",         value: counts.open,     icon: "error_outline", bg: "#fff8e1", color: "#e65100"  },
            { label: "Resolved",     value: counts.resolved, icon: "check_circle",  bg: "#f0fff4", color: TC.success },
            { label: "High / Critical", value: counts.high,  icon: "warning",       bg: "#fff0f0", color: TC.error   },
          ].map(c => (
            <div key={c.label} style={{
              flex: "1 1 140px", background: c.bg, border: "1px solid #d8e6f7",
              borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", gap: 10,
            }}>
              <Icon style={{ fontSize: 22, color: c.color }}>{c.icon}</Icon>
              <div>
                <div style={{ fontSize: "1.3rem", fontWeight: FW.bold, color: TC.primary, lineHeight: 1 }}>{c.value}</div>
                <div style={{ fontSize: FS.xs, color: TC.tertiary, marginTop: 2 }}>{c.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Filter chips */}
        <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
          {[
            { key: "all",         label: "All" },
            { key: "open",        label: "Open" },
            { key: "resolved",    label: "Resolved" },
            { key: "high",        label: "High Severity" },
            { key: "in_progress", label: "In Progress" },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              style={{
                padding: "5px 14px", borderRadius: 20, fontSize: FS.xs, fontWeight: FW.medium,
                cursor: "pointer", border: "1.5px solid",
                borderColor: filter === f.key ? TC.link : "#d8e6f7",
                background: filter === f.key ? "#e8f0fb" : "#ffffff",
                color: filter === f.key ? TC.link : TC.secondary,
                transition: "all 0.15s",
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Issue list */}
        {filtered.length === 0 ? (
          <div style={{ textAlign: "center", color: TC.tertiary, fontSize: FS.sm, padding: "40px 0" }}>
            No issues match this filter.
          </div>
        ) : (
          filtered.map(issue => <IssueCard key={issue.id} issue={issue} />)
        )}

      </VuiBox>
    </DashboardLayout>
  );
}

